/**
 * i18next + i18next-vue Plugin Setup
 *
 * Configures i18next with:
 * - HTTP backend to fetch translation bundles from the locale API
 *   (GET /api/v1/locale/:domain — PUBLIC, domain = window.location.hostname)
 * - localStorage bundle cache for instant page-reload and offline resilience
 * - Language is always 'en'; actual translations come from the /:domain API
 *
 * All translations come from the backend (tms-api-locale). The English
 * bundle from @tms-phoenix/tms-types is embedded at build time as a
 * static fallback — if the API is unreachable, the app renders English
 * translations instead of raw keys.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 3-TIER CLIENT-SIDE CACHING STRATEGY
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ┌─────────┬────────────────────────────────────────────┬──────────────────┐
 * │ Layer   │ Where                                      │ TTL / Lifetime   │
 * ├─────────┼────────────────────────────────────────────┼──────────────────┤
 * │ L1      │ i18next in-memory store                    │ Tab session      │
 * │ L2      │ localStorage (tms-locale-bundle-{lang})    │ Persistent       │
 * │ L3      │ Browser HTTP cache (Cache-Control: 300s)   │ 5 min            │
 * └─────────┴────────────────────────────────────────────┴──────────────────┘
 *
 * On app boot:
 *   1. If L2 (localStorage) has a cached bundle → i18next initialises
 *      synchronously with cached translations (no blocking HTTP wait).
 *      A background fetch refreshes the bundle from the API afterwards.
 *   2. If no L2 cache → HTTP backend fires GET /api/v1/locale/:domain
 *      with retries: 0 + maxRetries: 0 (single attempt, no retries).
 *      → cache HIT (< 5 min): instant, no network
 *      → cache MISS: CDN/API fetch → response cached by browser
 *   3. Fetched bundle is saved to localStorage (L2) and i18next store (L1)
 *   4. On next page reload: L2 is loaded immediately, HTTP fetch runs in background
 *
 * After admin override change:
 *   The write endpoint returns the freshly regenerated bundle, and
 *   applyFreshBundle() puts it straight into L1 and L2. Don't follow a write
 *   with reloadResources()/reloadTranslationsPostAuth(): `cache: 'reload'`
 *   only bypasses L3 (the browser cache) — the CDN can still serve the old
 *   bundle for up to 5 minutes, and that stale response would overwrite L1
 *   and L2, visibly reverting the edit.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Domain-based locale resolution:
 *   The hostname (e.g. tenant1dev.staging.timelesslabs.com) is passed as the
 *   :domain param. The backend resolves the tenant and returns the correct
 *   locale bundle. Works identically pre-auth and post-auth.
 *
 * @see https://www.i18next.com/
 * @see https://github.com/i18next/i18next-vue
 */
import i18next from 'i18next';
import I18NextVue from 'i18next-vue';
import HttpBackend from 'i18next-http-backend';
import type { App } from 'vue';

import { config, API_ROUTES } from '@/core/config';
// Import only the English fallback directly from the JSON file.
// This avoids pulling all 6 locale bundles (~958 KB) through the
// @tms-phoenix/tms-types barrel — only ~183 KB is loaded at startup.
import enFallbackBundle from '@tms-phoenix/tms-types/locales/en';
// Leaf module — safe to import here. The composable that owns the runtime
// value (shared/composables/useMrnLabel) imports THIS plugin, so pulling the
// constants from it instead would create a cycle.
import { MRN_LABEL_FALLBACK, MRN_LABEL_VAR } from '@/shared/utils/mrn';
import { keyMarkerPostProcessor } from '@/shared/utils/key-markers';

// ── Constants ───────────────────────────────────────────────────────────────

/** Default / fallback language — always 'en'. Actual translations come from /:domain API. */
export const DEFAULT_LANGUAGE = 'en';

/**
 * Supported languages currently shipped by the SPA.
 * Kept for type compatibility but language switching is not used.
 */
export const SUPPORTED_LANGUAGES = ['en', 'en-GB', 'en-US', 'de', 'fr'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** @deprecated No longer used — no language switching. Kept for backward compat. */
export const LANGUAGE_STORAGE_KEY = 'tms-locale-lang';

/**
 * Namespace used for the primary translation bundle.
 * Additional namespaces (e.g. 'validation', 'clinical') can be added later.
 */
export const DEFAULT_NAMESPACE = 'translation';

// ── localStorage Bundle Cache (L2) ─────────────────────────────────────────

/**
 * localStorage key prefix for cached translation bundles.
 *
 * Format: tms-locale-bundle-{lang}
 * Value:  JSON string of { domain, timestamp, bundle }
 *
 * This is the L2 cache layer — persists across page reloads and browser
 * restarts. Updated every time a fresh bundle is fetched from the API.
 * Read at app boot to immediately hydrate i18next before the HTTP fetch
 * completes — eliminates the flash of untranslated content on page reload.
 */
const BUNDLE_CACHE_PREFIX = 'tms-locale-bundle';

/** Maximum age of a cached bundle (24 hours). After this, the cache is stale. */
const BUNDLE_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface CachedBundle {
  domain: string;
  timestamp: number;
  bundle: Record<string, string>;
}

/**
 * Save a fetched bundle to localStorage (L2 cache).
 * Called by the HTTP backend parse() callback after every successful API fetch.
 */
function saveBundleToCache(lang: string, bundle: Record<string, string>): void {
  try {
    const entry: CachedBundle = {
      domain: getLocaleDomain(),
      timestamp: Date.now(),
      bundle,
    };
    localStorage.setItem(`${BUNDLE_CACHE_PREFIX}-${lang}`, JSON.stringify(entry));
  } catch {
    // localStorage full or unavailable — non-fatal
  }
}

/**
 * Load a cached bundle from localStorage (L2 cache).
 * Returns null if cache is missing, expired, or for a different tenant.
 */
function loadBundleFromCache(lang: string): Record<string, string> | null {
  try {
    const raw = localStorage.getItem(`${BUNDLE_CACHE_PREFIX}-${lang}`);
    if (!raw) return null;

    const entry: CachedBundle = JSON.parse(raw);

    // Check staleness
    if (Date.now() - entry.timestamp > BUNDLE_CACHE_MAX_AGE_MS) {
      localStorage.removeItem(`${BUNDLE_CACHE_PREFIX}-${lang}`);
      return null;
    }

    // Check domain match — cached bundle must be from the same domain.
    // This prevents leaking one domain's translations into another session.
    const currentDomain = getLocaleDomain();
    if (currentDomain !== entry.domain) {
      return null;
    }

    return entry.bundle;
  } catch {
    return null;
  }
}

/** Clear all cached bundles (called on logout). */
export function clearBundleCache(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(BUNDLE_CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // Non-fatal
  }
}

// ── Backend configuration builder ───────────────────────────────────────────

/**
 * Build the `i18next-http-backend` loadPath dynamically.
 *
 * Points to the locale API domain-based bundle endpoint:
 *   GET /api/v1/locale/:domain  (PUBLIC, raw flat JSON)
 *
 * Routed through the gateway via the LOCALE service prefix:
 *   {baseUrl}/locale/api/v1/locale/{domain}
 *
 * The :domain param is the full hostname (e.g. tenant1dev.staging.timelesslabs.com).
 * The backend resolves the tenant from the domain and returns the correct bundle.
 */

/**
 * Resolve the domain to use for the locale API.
 * Always returns `window.location.hostname` so the backend resolves the
 * correct tenant — works identically in deployed and local environments.
 */
function getLocaleDomain(): string {
  if (typeof window !== 'undefined') {
    return window.location.hostname;
  }
  // SSR / non-browser fallback (should never happen in the SPA)
  return 'localhost';
}

/** @deprecated No longer needed — domain-based locale resolution doesn't require tenant context. */
export function setTenantContext(_tenantId: string): void {
  // No-op: kept for backward compatibility with locale store.
  // The domain-based API resolves the tenant from the hostname.
}

/** @deprecated No longer needed — domain-based locale resolution doesn't require tenant context. */
export function clearTenantContext(): void {
  // No-op: kept for backward compatibility with locale store.
}

function buildLoadPath(_languages: readonly string[], _namespaces: readonly string[]): string {
  const baseUrl = config.api.baseUrl.replace(/\/+$/, '');
  const domain = getLocaleDomain();

  // Domain-based bundle: GET /locale/api/v1/locale/{domain}
  // Returns raw flat JSON — the backend resolves tenant from the domain
  return `${baseUrl}${API_ROUTES.LOCALE}/locale/${encodeURIComponent(domain)}`;
}

/**
 * Shared parse callback for i18next-http-backend.
 * Handles both raw flat JSON (/:domain) and enveloped JSON (/map) responses,
 * then persists the result to the localStorage L2 cache.
 */
function buildParseCallback() {
  return (data: string, _url: string, _key: string, lang?: string) => {
    try {
      const json = JSON.parse(data);
      let bundle: Record<string, string>;

      if (json.success === true && json.data && typeof json.data === 'object') {
        bundle = json.data;
      } else {
        bundle = json;
      }

      if (lang && Object.keys(bundle).length > 0) {
        saveBundleToCache(lang, bundle);
      }

      return bundle;
    } catch {
      return {};
    }
  };
}

/**
 * Fetch fresh translations from the API in the background (fire-and-forget).
 * Used by the fast-path when L2 cache already provided instant translations.
 * On success the L2 cache and i18next in-memory store are both refreshed.
 */
function refreshBundleInBackground(): void {
  const url = buildLoadPath([], []);

  // PHX-1180: the httpOnly auth cookie is sent automatically (same-origin
  // fetch defaults to `credentials: 'same-origin'`) — no Authorization header needed.
  fetch(url, {
    cache: import.meta.env.DEV ? 'no-cache' : 'default',
  })
    .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
    .then((text) => {
      const parse = buildParseCallback();
      const bundle = parse(text, url, '', 'en');
      if (bundle && Object.keys(bundle).length > 0) {
        // Merge fresh translations into the live i18next store.
        // deep=true, overwrite=true — API values win over cache.
        i18next.addResourceBundle('en', DEFAULT_NAMESPACE, bundle, true, true);
      }
    })
    .catch(() => {
      // Non-fatal — cached bundle is already active.
    });
}

// ── i18next initialization ──────────────────────────────────────────────────

/**
 * Initialize i18next instance.
 *
 * Called once at app startup. All translations come from the backend API.
 * On first visit, `await i18next.init()` blocks until the HTTP fetch
 * completes (or fails). On subsequent visits, the localStorage (L2) cache
 * provides instant translations while the HTTP fetch refreshes in the
 * background.
 *
 * If the domain cannot be resolved or the API is unreachable, i18next
 * falls back to the embedded English bundle from @tms-phoenix/tms-types.
 */
export async function initI18n(): Promise<typeof i18next> {
  // Always use 'en' as the i18next language key.
  // Actual translated values come from the /:domain API response.
  // If the API fails or returns empty, the static 'en' fallback is used.
  const resolvedLng = DEFAULT_LANGUAGE;

  // ── L2: Read localStorage cache ─────────────────────────────────────
  const cachedBundle = loadBundleFromCache('en');

  // ── Fast-path: if we have an L2 cached bundle, initialise i18next
  // synchronously with the cached resources and refresh from the API in
  // the background.  This eliminates the blocking HTTP wait on reload.
  if (cachedBundle && Object.keys(cachedBundle).length > 0) {
    i18next
      .use(HttpBackend)
      .use(keyMarkerPostProcessor)
      .init({
        lng: resolvedLng,
        fallbackLng: DEFAULT_LANGUAGE,
        supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
        defaultNS: DEFAULT_NAMESPACE,
        ns: [DEFAULT_NAMESPACE],
        // keyMarkerPostProcessor (shared/utils/key-markers.ts) is always
        // registered; its own `keyMarkersEnabled` flag (default off) gates
        // whether it actually appends anything, so inspect mode never needs
        // to touch this options object. postProcessPassResolved feeds it
        // `i18nResolved.exactUsedKey`.
        postProcess: ['keyMarker'],
        postProcessPassResolved: true,
        // Provide cached bundle as initial resources so init resolves
        // immediately — no HTTP fetch blocking.
        resources: {
          en: { [DEFAULT_NAMESPACE]: cachedBundle },
        },
        partialBundledLanguages: true,
        interpolation: {
          escapeValue: false,
          // Seeds {{mrnLabel}} so MRN strings render a real term on the very
          // first paint. Refined per-tenant by setMrnLabel() once the
          // (pre-auth) tenant config resolves — see useMrnLabel.
          defaultVariables: { [MRN_LABEL_VAR]: MRN_LABEL_FALLBACK },
        },
        backend: {
          loadPath: buildLoadPath,
          parse: buildParseCallback(),
          requestOptions: {
            cache: import.meta.env.DEV ? 'no-cache' : 'default',
          },
          retries: 0,
          retryDelay: 200,
        },
        load: 'currentOnly',
        // ── BackendConnector retry policy ────────────────────────────────
        // i18next core retries failed backend reads up to maxRetries times
        // (default 5). Disable so the /:domain API is called at most once.
        maxRetries: 0,
        debug: import.meta.env.DEV,
        returnNull: false,
        returnEmptyString: false,
        saveMissing: false,
        missingKeyHandler: import.meta.env.DEV
          ? (_lngs: readonly string[], _ns: string, key: string) => {
              console.warn(`[i18n] Missing translation key: "${key}"`);
            }
          : undefined,
      });

    // Seed the static English fallback underneath (overwrite=false)
    i18next.addResourceBundle(
      'en',
      DEFAULT_NAMESPACE,
      enFallbackBundle as unknown as Record<string, string>,
      true,
      false,
    );

    // ── Background refresh: fetch fresh bundle from API without
    // blocking the UI thread.  Fire-and-forget.
    refreshBundleInBackground();

    return i18next;
  }

  // ── Slow-path: no L2 cache — must wait for the HTTP fetch.
  await i18next
    .use(HttpBackend)
    .use(keyMarkerPostProcessor)
    .init({
      // ── Language ───────────────────────────────────────────────────────
      lng: resolvedLng,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],

      // ── Namespaces ────────────────────────────────────────────────────
      defaultNS: DEFAULT_NAMESPACE,
      ns: [DEFAULT_NAMESPACE],

      // keyMarkerPostProcessor (shared/utils/key-markers.ts) is always
      // registered; its own `keyMarkersEnabled` flag (default off) gates
      // whether it actually appends anything, so inspect mode never needs
      // to touch this options object.
      postProcess: ['keyMarker'],
      postProcessPassResolved: true,

      // NOTE: Do NOT pass `resources` here. Providing resources for the
      // active lng+ns causes i18next to consider the namespace "loaded"
      // and skip the HTTP backend fetch entirely — even with
      // partialBundledLanguages: true. Instead, we seed via
      // addResourceBundle() after init (below) so the HTTP backend
      // always fires GET /:domain on every page load.

      // ── Interpolation ─────────────────────────────────────────────────
      interpolation: {
        escapeValue: false, // Vue already handles XSS
        // Seeds {{mrnLabel}} so MRN strings render a real term on the very
        // first paint. Refined per-tenant by setMrnLabel() once the
        // (pre-auth) tenant config resolves — see useMrnLabel.
        defaultVariables: { [MRN_LABEL_VAR]: MRN_LABEL_FALLBACK },
      },

      // ── HTTP Backend ──────────────────────────────────────────────────
      backend: {
        loadPath: buildLoadPath,
        parse: buildParseCallback(),
        // ── Browser HTTP cache (L3) ────────────────────────────────────
        // In production, let the browser respect Cache-Control headers from
        // the BE (max-age=300, s-maxage=300 → 5-min browser cache).
        // In development, use 'no-cache' so the browser always revalidates
        // with the server — avoids serving stale/empty cached bundles.
        requestOptions: {
          cache: import.meta.env.DEV ? 'no-cache' : 'default',
        },

        // ── Retry policy ──────────────────────────────────────────────────
        // Default is retries: 5 with exponential back-off — far too slow
        // during initial load when the locale API is unreachable.
        // Call the API once; on failure, immediately use the fallback bundle.
        retries: 0,
        retryDelay: 200,
      },

      // ── Behaviour ─────────────────────────────────────────────────────
      load: 'currentOnly', // only load 'en', not 'en-US' + 'en'
      // ── BackendConnector retry policy ──────────────────────────────────
      // i18next core retries failed backend reads up to maxRetries times
      // (default 5). Disable so the /:domain API is called at most once.
      maxRetries: 0,
      debug: import.meta.env.DEV,
      returnNull: false,
      returnEmptyString: false,

      // Silently handle missing keys in production; log in dev
      saveMissing: false,
      missingKeyHandler: import.meta.env.DEV
        ? (_lngs: readonly string[], _ns: string, key: string) => {
            console.warn(`[i18n] Missing translation key: "${key}"`);
          }
        : undefined,
    });

  // ── Post-init hydration ─────────────────────────────────────────────────
  // Always deep-merge the embedded English fallback as a base layer.
  // deep=true merges nested keys; overwrite=false keeps API-provided
  // values intact. This ensures newly added keys in enFallbackBundle are
  // available even when the locale API hasn't been seeded with them yet.
  i18next.addResourceBundle(
    'en',
    DEFAULT_NAMESPACE,
    enFallbackBundle as unknown as Record<string, string>,
    true,   // deep — merge recursively
    false,  // overwrite — do NOT replace existing (API) values
  );

  // If the API returned nothing at all, also layer the L2 cached bundle
  // on top (overwrite=true) so the last-known-good from localStorage wins.
  const currentStore = i18next.store?.data?.en?.translation;
  if (cachedBundle && (!currentStore || Object.keys(currentStore).length === 0)) {
    i18next.addResourceBundle('en', DEFAULT_NAMESPACE, cachedBundle, true, true);
  }

  return i18next;
}

// ── Vue plugin installer ────────────────────────────────────────────────────

/**
 * Install i18next-vue on the Vue app instance.
 *
 * Usage in main.ts:
 * ```ts
 * import { setupI18n } from '@/core/plugins/i18n';
 * const app = createApp(App);
 * await setupI18n(app);
 * ```
 */
export async function setupI18n(app: App): Promise<void> {
  const i18n = await initI18n();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- i18next-vue resolves a different @vue/runtime-core patch version
  app.use(I18NextVue as any, { i18next: i18n });
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Applies an already-fetched bundle directly to the live i18next instance —
 * no network round-trip, so there's no cache layer that can serve something
 * stale. Use this whenever a call already has a guaranteed-fresh bundle in
 * hand, e.g. the `bundle` field `PUT /locale/override` returns in its own
 * response (see settingsStore.saveLocaleOverrides): that response body *is*
 * the just-regenerated bundle, straight from the write that produced it, so
 * there's nothing to gain — and real staleness risk to lose — from instead
 * re-fetching it over HTTP via {@link reloadTranslationsPostAuth} (see that
 * function's docblock for why its own re-fetch isn't actually guaranteed
 * fresh). Also refreshes the L2 localStorage cache, so a reload immediately
 * after still shows this bundle before the next HTTP fetch completes.
 */
export function applyFreshBundle(bundle: Record<string, string>): void {
  i18next.addResourceBundle(i18next.language, DEFAULT_NAMESPACE, bundle, true, true);
  saveBundleToCache(i18next.language, bundle);
}

/**
 * Reload the translation bundle after login (post-auth) with full tenant context.
 *
 * Call this after successful authentication so the user gets the full
 * tenant-scoped translation bundle (with custom overrides) instead of
 * the pre-login subdomain-based bundle.
 *
 * Flow:
 *   1. This function temporarily sets cache='reload' to bypass L3 (browser HTTP cache)
 *   2. i18next re-fetches via HTTP backend from GET /api/v1/locale/:domain
 *   3. parse() saves the fresh bundle to localStorage (L2)
 *   4. i18next in-memory store (L1) is updated
 *   5. All reactive $t() bindings update automatically
 *
 * cache='reload' only bypasses L3 — the *browser's own* HTTP cache. It does
 * nothing about any cache the response passes through on its way here (a
 * CDN edge cache, a server-side cache) — GET /locale/:domain is served with
 * `Cache-Control: public, max-age=300, s-maxage=300` (see the backend route),
 * so a re-fetch through this function can still return a bundle up to 5
 * minutes stale. That's fine for a post-login reload (nothing just changed
 * seconds ago), but it's why an override save uses
 * {@link applyFreshBundle} instead of this function — saving, then
 * immediately re-fetching here, could silently overwrite the just-saved
 * value with the stale cached one for up to that same 5 minutes.
 */
export async function reloadTranslationsPostAuth(): Promise<void> {
  // Temporarily bypass browser HTTP cache (L3) for this reload
  // so we get a fresh bundle from CDN/API after login or override change.
  const backendOpts = i18next.options.backend as Record<string, unknown> | undefined;
  const originalCacheMode = (backendOpts?.requestOptions as Record<string, unknown>)?.cache;

  if (backendOpts?.requestOptions) {
    (backendOpts.requestOptions as Record<string, unknown>).cache = 'reload';
  }

  try {
    if (i18next.services?.backendConnector) {
      await i18next.reloadResources(i18next.language, DEFAULT_NAMESPACE);
    }
  } finally {
    // Restore normal cache mode for subsequent fetches
    if (backendOpts?.requestOptions) {
      (backendOpts.requestOptions as Record<string, unknown>).cache = originalCacheMode ?? 'default';
    }
  }
}

/**
 * @deprecated No-op — language switching is not supported.
 * The app always uses translations from the /:domain API (language = 'en').
 * Kept for backward compatibility with useLocale composable.
 */
export async function changeLanguage(_lang: SupportedLanguage): Promise<void> {
  // No-op: language switching is not supported.
  // The /:domain API determines the translations, not the i18next language key.
}

/** Get the current active language — always 'en'. */
export function getCurrentLanguage(): string {
  return DEFAULT_LANGUAGE;
}

/** Re-export the singleton i18next instance for advanced usage. */
export { i18next };
